/**
 * TelehealthReporting Component
 *
 * Dashboard with charts and metrics for telehealth sessions
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import {
  Video,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Download,
  RefreshCw,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { telehealthReportingAPI } from '@/services/api/telehealth';
import { useTabbedChrome } from '@/components/weavein';
import {
  TelehealthReportSummary,
  TelehealthReportingProps,
  SDOH_DOMAINS,
} from './types';

// Date range presets
const DATE_PRESETS = [
  { label: 'Last 7 days', getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Last 30 days', getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: 'This month', getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { label: 'Last month', getValue: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'Last 90 days', getValue: () => ({ start: subDays(new Date(), 90), end: new Date() }) },
];

export default function TelehealthReporting({ className = '' }: TelehealthReportingProps) {
  const tabbed = useTabbedChrome();
  const [dateRange, setDateRange] = useState(DATE_PRESETS[1].getValue());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Fetch report data
  const {
    data: report,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['telehealth-report', format(dateRange.start, 'yyyy-MM-dd'), format(dateRange.end, 'yyyy-MM-dd')],
    queryFn: () =>
      telehealthReportingAPI.getReportSummary(
        format(dateRange.start, 'yyyy-MM-dd'),
        format(dateRange.end, 'yyyy-MM-dd')
      ),
  });

  // Calculate percentage change (mock - would compare to previous period)
  const getChange = (value: number, baseline: number = 0) => {
    if (baseline === 0) return 0;
    return ((value - baseline) / baseline) * 100;
  };

  const handleExport = () => {
    if (!report) return;

    // Create CSV data
    const csvRows = [
      ['Telehealth Report'],
      [`Period: ${format(dateRange.start, 'MMM d, yyyy')} - ${format(dateRange.end, 'MMM d, yyyy')}`],
      [],
      ['Session Metrics'],
      ['Total Scheduled', report.sessions.total_scheduled.toString()],
      ['Total Completed', report.sessions.total_completed.toString()],
      ['No Shows', report.sessions.total_no_shows.toString()],
      ['Technical Failures', report.sessions.total_technical_failures.toString()],
      ['Completion Rate', `${(report.sessions.completion_rate * 100).toFixed(1)}%`],
      ['No Show Rate', `${(report.sessions.no_show_rate * 100).toFixed(1)}%`],
      ['Average Duration (min)', report.sessions.avg_duration_minutes.toFixed(1)],
      [],
      ['Encounter Metrics'],
      ['Total Documented', report.encounters.total_documented.toString()],
      ['Total Signed', report.encounters.total_signed.toString()],
      ['Draft Encounters', report.encounters.total_draft.toString()],
      ['Documentation Completion Rate', `${(report.encounters.documentation_completion_rate * 100).toFixed(1)}%`],
      [],
      ['SDOH Domains Addressed'],
      ...report.sdoh_domains.map((d) => [d.domain_label, d.count.toString(), `${d.percentage.toFixed(1)}%`]),
      [],
      ['By Coordinator'],
      ['Name', 'Sessions Completed', 'Avg Duration', 'Completion Rate'],
      ...report.by_coordinator.map((c) => [
        c.user_name,
        c.sessions_completed.toString(),
        c.avg_duration_minutes.toFixed(1),
        `${(c.completion_rate * 100).toFixed(1)}%`,
      ]),
    ];

    const csv = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telehealth-report-${format(dateRange.start, 'yyyy-MM-dd')}-${format(dateRange.end, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              {!tabbed && (
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  Telehealth Reports
                </h1>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {DATE_PRESETS.find(
                      (p) =>
                        format(p.getValue().start, 'yyyy-MM-dd') === format(dateRange.start, 'yyyy-MM-dd') &&
                        format(p.getValue().end, 'yyyy-MM-dd') === format(dateRange.end, 'yyyy-MM-dd')
                    )?.label || 'Custom Range'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>

                {showDatePicker && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                    {DATE_PRESETS.map((preset, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setDateRange(preset.getValue());
                          setShowDatePicker(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => refetch()}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>

              <button
                onClick={handleExport}
                disabled={!report}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isLoading ? (
          <div className="grid gap-6">
            {/* Loading skeletons */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 animate-pulse">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                  <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-4 rounded-lg">
            Failed to load report data. Please try again.
          </div>
        ) : report ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                icon={<Calendar className="h-5 w-5" />}
                label="Total Sessions"
                value={report.sessions.total_scheduled}
                color="blue"
              />
              <MetricCard
                icon={<CheckCircle2 className="h-5 w-5" />}
                label="Completed"
                value={report.sessions.total_completed}
                subValue={`${(report.sessions.completion_rate * 100).toFixed(0)}% rate`}
                color="green"
              />
              <MetricCard
                icon={<XCircle className="h-5 w-5" />}
                label="No Shows"
                value={report.sessions.total_no_shows}
                subValue={`${(report.sessions.no_show_rate * 100).toFixed(0)}% rate`}
                color="red"
              />
              <MetricCard
                icon={<Clock className="h-5 w-5" />}
                label="Avg Duration"
                value={`${Math.round(report.sessions.avg_duration_minutes)} min`}
                color="purple"
              />
            </div>

            {/* Charts Row */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Session Outcomes */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-gray-500" />
                  Session Outcomes
                </h3>
                <div className="space-y-4">
                  <OutcomeBar
                    label="Completed"
                    value={report.sessions.total_completed}
                    total={report.sessions.total_scheduled}
                    color="bg-green-500"
                  />
                  <OutcomeBar
                    label="No Shows"
                    value={report.sessions.total_no_shows}
                    total={report.sessions.total_scheduled}
                    color="bg-red-500"
                  />
                  <OutcomeBar
                    label="Cancelled"
                    value={report.sessions.total_cancelled}
                    total={report.sessions.total_scheduled}
                    color="bg-gray-500"
                  />
                  <OutcomeBar
                    label="Technical Failures"
                    value={report.sessions.total_technical_failures}
                    total={report.sessions.total_scheduled}
                    color="bg-orange-500"
                  />
                </div>
              </div>

              {/* SDOH Domains */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-gray-500" />
                  SDOH Domains Addressed
                </h3>
                <div className="space-y-3">
                  {report.sdoh_domains.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                      No SDOH data recorded yet
                    </p>
                  ) : (
                    report.sdoh_domains.map((domain) => {
                      const domainInfo = SDOH_DOMAINS.find((d) => d.code === domain.domain);
                      return (
                        <div key={domain.domain} className="flex items-center gap-3">
                          <div className="w-32 text-sm text-gray-700 dark:text-gray-300 truncate">
                            {domainInfo?.label || domain.domain_label}
                          </div>
                          <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full transition-all duration-500"
                              style={{ width: `${domain.percentage}%` }}
                            />
                          </div>
                          <div className="w-12 text-right text-sm text-gray-500 dark:text-gray-400">
                            {domain.count}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Encounter Metrics */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Encounter Documentation
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {report.encounters.total_documented}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Documented</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
                    {report.encounters.total_signed}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Signed</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <p className="text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
                    {report.encounters.total_draft}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Draft</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {(report.encounters.documentation_completion_rate * 100).toFixed(0)}%
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Completion Rate</p>
                </div>
              </div>
            </div>

            {/* Coordinator Performance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-500" />
                  Performance by Coordinator
                </h3>
              </div>
              {report.by_coordinator.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                  No coordinator data available for this period
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Coordinator
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Sessions Completed
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Avg Duration
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Completion Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {report.by_coordinator.map((coordinator) => (
                        <tr key={coordinator.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-medium text-sm">
                                {coordinator.user_name.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {coordinator.user_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-700 dark:text-gray-300">
                            {coordinator.sessions_completed}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-700 dark:text-gray-300">
                            {Math.round(coordinator.avg_duration_minutes)} min
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                coordinator.completion_rate >= 0.9
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : coordinator.completion_rate >= 0.7
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              }`}
                            >
                              {(coordinator.completion_rate * 100).toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Metric Card Component
interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  color: 'blue' | 'green' | 'red' | 'purple' | 'yellow';
}

function MetricCard({ icon, label, value, subValue, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
          {subValue && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Outcome Bar Component
interface OutcomeBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

function OutcomeBar({ label, value, total, color }: OutcomeBarProps) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {value} ({percentage.toFixed(0)}%)
        </span>
      </div>
      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
